import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { DialogFooter } from "@/components/ui/dialog"
import { PERMISSIONS } from "@/constants/permissions"
import { usePermissionGuard } from "@/hooks/usePermissionGuard"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"
import { getApiErrorStatus } from "../../../api/apiError"
import { useCreateNote } from "../../../api/notesApi"
import type {
  SupplierInquiryDetail,
  useCloseInquiry,
} from "../../../api/supplierInquiriesApi"
import {
  NoteDraftFields,
  type NoteDraft,
} from "../../shared/notes/NoteComposer"

/**
 * The draft and what the close flow has done with it so far. The dialog's owner
 * keeps it: the form unmounts with the dialog, and a close that failed after a
 * note went through is retried from where it stopped, so reopening must show the
 * same draft and never write either note twice.
 */
export interface CloseFlow {
  /** The reason and its files, kept as typed even after the inquiry note is saved. */
  draft: NoteDraft
  /** The "also warn on the supplier" box: a warning that is still owed survives a dismiss. */
  warnSupplier: boolean
  inquiryNoteId: string | null
  supplierNoteWritten: boolean
  /** Notes are being written; with a pending close this is when the dialog must stay open. */
  writing: boolean
}

export const IDLE_CLOSE_FLOW: CloseFlow = {
  draft: { body: "", files: [] },
  warnSupplier: false,
  inquiryNoteId: null,
  supplierNoteWritten: false,
  writing: false,
}

interface InquiryCloseNoteFormProps {
  inquiry: SupplierInquiryDetail
  closeInquiry: ReturnType<typeof useCloseInquiry>
  flow: CloseFlow
  onFlowChange: (patch: Partial<CloseFlow>) => void
  /** The inquiry is closed, or turned out to be closed already: leave the dialog. */
  onDone: () => void
  onCancel: () => void
}

/**
 * The close dialog's body once MC_NOTES is on: an optional reason with attachments,
 * and the footer. The reason is written as an inquiry note first and the close then
 * names it, so the timeline shows the text. Closing without a reason sends the same
 * empty body as before.
 *
 * Lives inside the dialog content and keeps no state of its own: the draft and
 * the written notes are the dialog owner's, see {@link CloseFlow}.
 */
export function InquiryCloseNoteForm({
  inquiry,
  closeInquiry,
  flow,
  onFlowChange,
  onDone,
  onCancel,
}: InquiryCloseNoteFormProps) {
  const { translate } = useTranslation()
  const canEditSuppliers = usePermissionGuard(
    PERMISSIONS.MC_MASTER_DATA_EDIT,
  ).canRun
  const createInquiryNote = useCreateNote("inquiry")
  const createSupplierNote = useCreateNote("supplier")

  const { draft, warnSupplier, inquiryNoteId, supplierNoteWritten, writing } =
    flow

  const reason = draft.body.trim()
  // The server refuses an empty note, so a file needs words to ride on.
  const filesNeedReason = draft.files.length > 0 && reason.length === 0
  const supplierId = inquiry.supplierId
  const canWarn = canEditSuppliers && !!supplierId
  const busy = writing || closeInquiry.isPending

  async function confirm() {
    onFlowChange({ writing: true })
    try {
      let noteId = inquiryNoteId
      if (!noteId && reason) {
        const note = await createInquiryNote.mutateAsync({
          entityType: "inquiry",
          entityId: inquiry.id,
          body: reason,
          files: draft.files,
        })
        noteId = note.id
        onFlowChange({ inquiryNoteId: note.id })
      }
      if (
        canEditSuppliers &&
        supplierId &&
        warnSupplier &&
        reason &&
        !supplierNoteWritten
      ) {
        // Text only: the evidence stays on the inquiry note.
        await createSupplierNote.mutateAsync({
          entityType: "supplier",
          entityId: supplierId,
          body: reason,
          warnOnInquiry: true,
        })
        onFlowChange({ supplierNoteWritten: true })
      }
      closeInquiry.mutate(
        { id: inquiry.id, ...(noteId ? { body: { noteId } } : {}) },
        {
          onSuccess: onDone,
          // The hook has toasted. A 409 with a note means the inquiry is closed already
          // and the note is saved: nothing is left to retry here.
          onError: (error) => {
            if (noteId && getApiErrorStatus(error) === 409) onDone()
          },
        },
      )
    } catch {
      // A note failed and its hook has toasted: the inquiry is not closed.
    } finally {
      onFlowChange({ writing: false })
    }
  }

  return (
    <>
      <NoteDraftFields
        draft={draft}
        onChange={(next) => onFlowChange({ draft: next })}
        label={translate("MC_NOTE_CLOSE_REASON_LABEL")}
        placeholder={translate("MC_NOTE_CLOSE_REASON_PLACEHOLDER")}
        disabled={busy || inquiryNoteId !== null}
        autoFocus
      />
      {filesNeedReason && (
        <p className="text-[12px] text-attention">
          {translate("MC_NOTE_CLOSE_FILES_NEED_REASON")}
        </p>
      )}
      {canWarn && (
        <label className="flex items-center gap-2 text-[12.5px]">
          <Checkbox
            checked={warnSupplier}
            onCheckedChange={(checked) =>
              onFlowChange({ warnSupplier: checked === true })
            }
            disabled={busy || supplierNoteWritten || reason.length === 0}
          />
          {translate("MC_NOTE_CLOSE_WARN_SUPPLIER")}
        </label>
      )}
      {inquiryNoteId !== null && (
        <p className="text-[12px] text-grey5">
          {translate(
            canWarn && warnSupplier && !supplierNoteWritten
              ? "MC_NOTE_CLOSE_SAVED_WARNING_PENDING"
              : "MC_NOTE_CLOSE_SAVED",
          )}
        </p>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={busy}>
          {translate("MC_DECL_BTN_CANCEL")}
        </Button>
        <Button
          variant="destructive"
          onClick={() => void confirm()}
          disabled={busy || filesNeedReason}
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          {translate("MC_INQ_CLOSE_REQUEST")}
        </Button>
      </DialogFooter>
    </>
  )
}
